import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { WhoIsInPanel } from '../attendance/WhoIsInPanel'
import { countByStatus, INTAKE_STATS, sortIntakeOrders } from './intake'
import { OrderCard } from './OrderCard'
import { orderMatchesText } from './orderSearch'
import { useOrdersList } from './useOrders'
import { localYmd } from './workshop'

/**
 * Serviceannahme: Status-Kennzahlen, „Wer ist da?"-Panel (Team-Anwesenheit heute), Suche, „Neuer Auftrag"
 * und die nach Dringlichkeit sortierte Auftragsliste.
 */
export function IntakeBoard() {
  const { data, isLoading, error } = useOrdersList()
  const [query, setQuery] = useState('')
  const [hideDone, setHideDone] = useState(false)
  // Heute einmal beim Mounten festhalten (für die Überfällig-Sortierung), nicht bei jedem Render neu.
  const today = useMemo(() => localYmd(new Date()), [])

  const visible = useMemo(() => {
    const all = data?.orders ?? []
    const q = query.trim().toLowerCase()
    let list = sortIntakeOrders(all, today)
    if (hideDone) list = list.filter((order) => order.status !== 'abgeholt')
    if (q) list = list.filter((order) => orderMatchesText(order, q))
    return list
  }, [data, query, hideDone, today])

  if (isLoading) return <div className="muted">Aufträge werden geladen…</div>
  if (error) {
    return (
      <div className="error" role="alert">
        Aufträge konnten nicht geladen werden.
      </div>
    )
  }

  const orders = data?.orders ?? []
  const abgeholt = countByStatus(orders, 'abgeholt')

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">Serviceannahme</h2>
          <p className="sub">Alle Aufträge · Fahrzeuge aufnehmen &amp; zuweisen</p>
        </div>
        <div className="wf-viewhead__actions">
          {abgeholt > 0 ? (
            <button type="button" className="wf-btn wf-btn--ghost" onClick={() => setHideDone((v) => !v)}>
              {hideDone ? `Abgeholte zeigen (${abgeholt})` : `Abgeholte ausblenden (${abgeholt})`}
            </button>
          ) : null}
          <Link to="/new" className="wf-btn wf-btn--primary">
            ＋ Neuer Auftrag
          </Link>
        </div>
      </div>

      <div className="grid cards4">
        {INTAKE_STATS.map((stat) => (
          <div key={stat.status} className={`wf-stat wf-stat--${stat.status}`}>
            <div className="wf-stat__value">{countByStatus(orders, stat.status)}</div>
            <div className="wf-stat__sub">{stat.label}</div>
          </div>
        ))}
      </div>

      <WhoIsInPanel />

      <input
        className="wf-input wf-intake__search"
        type="search"
        placeholder="Suchen: Nummer oder Notiz"
        aria-label="Aufträge durchsuchen"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      <div className="wf-sectionhead">
        <h3>Auftragsliste</h3>
        <span className="wf-count">{visible.length}</span>
      </div>

      {visible.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">{orders.length === 0 ? 'Noch keine Aufträge' : 'Keine Treffer'}</div>
          <div className="wf-empty__text">
            {orders.length === 0
              ? 'Sobald Aufträge angelegt sind, erscheinen sie hier.'
              : 'Andere Suche oder Filter versuchen.'}
          </div>
        </div>
      ) : (
        <div className="wf-cardgrid">
          {visible.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}
    </section>
  )
}
